import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Upload, FileCheck2, Sparkles, ShieldCheck, TrendingUp, AlertTriangle,
  ArrowRight, Loader2, ExternalLink, Info, CheckCircle2, XCircle,
  Percent, DollarSign, Calendar, ScrollText, Building2, Search, Target,
} from "lucide-react";
import CreditReportView, { computeScoreDeltas } from "@/components/app/CreditReportView";
import CreditReportHowTo from "@/components/app/CreditReportHowTo";

const SUPPORTED = ["Credit Karma", "IdentityIQ", "annualcreditreport.com", "Experian", "Equifax", "TransUnion"];

function fmtMoney(n) {
  if (n === null || n === undefined) return "—";
  return "$" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
}
function fmtPct(n) {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(1)}%`;
}
function scoreBand(s) {
  if (!s) return { label: "—", color: "#94a3b8" };
  if (s >= 800) return { label: "Excellent", color: "#10b981" };
  if (s >= 740) return { label: "Very Good", color: "#22c55e" };
  if (s >= 670) return { label: "Good", color: "#38bdf8" };
  if (s >= 580) return { label: "Fair", color: "#f59e0b" };
  return { label: "Poor", color: "#ef4444" };
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function MyCreditReport({ onGoToOpportunities }) {
  const [report, setReport] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/funding/credit-report/me");
      setReport(data?.report || null);
      setPrevious(data?.previous || null);
      setHistory([]);  // history-list endpoint used only for the "N reports" pill
      if ((data?.total || 0) > 0) {
        try {
          const h = await api.get("/funding/credit-report/history");
          setHistory(h.data?.reports || []);
        } catch {}
      }
    } catch (e) {
      // silent
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload a PDF export of your credit report");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("PDF is over 15MB — try re-exporting a smaller version");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/funding/credit-report/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success("Report analyzed — your data is live!", { duration: 5000 });
      await load();
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail) || e.message;
      toast.error(detail || "We couldn't read that file", { duration: 8000 });
    } finally { setUploading(false); }
  };

  if (loading) {
    return <div className="min-h-[400px] flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" size={16} /> Loading your report…</div>;
  }

  if (!report) {
    return (
      <div className="space-y-6" data-testid="credit-report-empty">
        {/* HERO — heavily marketed */}
        <div className="relative rounded-3xl overflow-hidden border border-amber-500/20 glow-amber" data-testid="credit-report-hero">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 via-transparent to-emerald-500/10" />
          <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-amber-500/15 blur-3xl" />
          <div className="relative p-6 sm:p-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-[10px] uppercase tracking-widest font-bold mb-4">
              <Sparkles size={12} /> Brand-new · Free with your account
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-black text-white leading-tight">
              Turn your real credit report<br />into <span className="text-amber-400">funding-ready data</span> in seconds.
            </h2>
            <p className="text-slate-300 mt-3 max-w-2xl text-base sm:text-lg">
              Drop the PDF of your credit report (Credit Karma, IdentityIQ, Experian, annualcreditreport.com — anywhere). Our AI reads it, extracts every tradeline, score, and inquiry, and unlocks a Funding Center powered by <span className="font-bold text-white">your actual numbers</span> — not guesses.
            </p>

            {/* Feature strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
              {[
                { icon: FileCheck2, label: "Any bureau" },
                { icon: ShieldCheck, label: "Never leaves your file" },
                { icon: TrendingUp, label: "Live funding matches" },
                { icon: Sparkles, label: "60-second analysis" },
              ].map((f, i) => (
                <div key={i} className="glass rounded-xl p-3 flex items-center gap-2 text-sm">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/20 text-amber-300 flex items-center justify-center"><f.icon size={14} /></div>
                  <span className="text-slate-200 font-semibold">{f.label}</span>
                </div>
              ))}
            </div>

            {/* Dropzone */}
            <div
              id="cq-credit-drop"
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; handleFile(f); }}
              onClick={() => !uploading && fileRef.current?.click()}
              className={`mt-6 rounded-2xl border-2 border-dashed p-8 sm:p-10 text-center cursor-pointer transition ${drag ? "border-amber-400 bg-amber-500/10" : "border-white/20 bg-black/20 hover:border-amber-400/60 hover:bg-amber-500/5"}`}
              data-testid="credit-report-dropzone"
            >
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="credit-report-file-input"
              />
              {uploading ? (
                <div className="flex flex-col items-center gap-3 text-white">
                  <Loader2 className="animate-spin" size={32} />
                  <p className="text-lg font-bold">Reading your report…</p>
                  <p className="text-sm text-slate-400">This takes 20-60 seconds. Please don't close the tab.</p>
                </div>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-2xl bg-amber-500 text-white flex items-center justify-center mx-auto mb-3 shadow-lg shadow-amber-500/30">
                    <Upload size={26} />
                  </div>
                  <p className="text-white text-lg font-bold">Drag & drop your credit report PDF here</p>
                  <p className="text-slate-400 text-sm mt-1">or click to choose a file · PDF only · max 15MB</p>
                </>
              )}
            </div>

            {/* Prominent "Don't know how?" CTA — the whole point is that this is impossible to miss */}
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl bg-amber-500/10 border border-amber-500/30 p-3">
              <div className="flex-1">
                <p className="text-white font-black">Not sure how to get your report?</p>
                <p className="text-slate-300 text-xs sm:text-sm mt-0.5">
                  We have a step-by-step guide for Credit Karma, IdentityIQ, Experian, and annualcreditreport.com — takes 2 minutes.
                </p>
              </div>
              <CreditReportHowTo
                onReady={() => document.getElementById("cq-credit-drop")?.scrollIntoView({ behavior: "smooth", block: "center" })}
              />
            </div>

            {/* Where to grab a PDF */}
            <p className="mt-5 text-[10px] uppercase tracking-widest font-bold text-slate-400">Or jump straight in — pick a source</p>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              {[
                { name: "Credit Karma", url: "https://www.creditkarma.com/my/credit-reports", tip: "Menu → Credit report → Save as PDF" },
                { name: "IdentityIQ", url: "https://member.identityiq.com/", tip: "3-bureau report → Export PDF" },
                { name: "Experian", url: "https://usa.experian.com/login/index", tip: "Login → Reports → Download PDF" },
                { name: "annualcreditreport.com", url: "https://www.annualcreditreport.com/", tip: "Free 1x/yr — save each bureau PDF" },
              ].map((s) => (
                <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" className="glass rounded-xl p-3 hover:border-amber-400/40 border-transparent border transition group" data-testid={`grab-pdf-${s.name.split(" ").join("-")}`}>
                  <div className="flex items-center gap-1 text-slate-100 font-semibold">
                    {s.name} <ExternalLink size={10} className="opacity-60 group-hover:opacity-100 transition" />
                  </div>
                  <p className="text-slate-400 mt-1">{s.tip}</p>
                </a>
              ))}
            </div>

            <div className="mt-5 flex items-start gap-2 text-[11px] text-slate-400 max-w-2xl">
              <ShieldCheck size={14} className="mt-0.5 shrink-0 text-emerald-400" />
              <p>Your report is stored securely on your Credit Quack file — visible only to you and your assigned specialist. We never sell or share credit data.</p>
            </div>
          </div>
        </div>

        {/* Why bother strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="credit-report-benefits">
          <BenefitCard icon={Target} title="Real funding matches" body="Every card, LOC, and lender in the Opportunities feed is filtered against your actual credit profile — not estimates." />
          <BenefitCard icon={AlertTriangle} title="Instant issue detection" body="High-utilization accounts, hard inquiries, or dispute-worthy items are surfaced automatically to your specialist." />
          <BenefitCard icon={TrendingUp} title="Track your progress" body="Upload a fresh report anytime. We compare score changes over time so you see the wins as you climb." />
        </div>
      </div>
    );
  }

  // We have a parsed report — render it via the shared view + top actions
  const deltas = computeScoreDeltas(report, previous);

  return (
    <div className="space-y-4" data-testid="credit-report-loaded">
      {/* Top action bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          {history.length > 1 && (
            <p className="text-xs text-slate-400">
              Report <span className="font-semibold text-white">{history.length}</span> of <span className="font-semibold text-white">{history.length}</span> ·
              {" "}first upload {fmtDate(history[history.length - 1]?.created_at)}
            </p>
          )}
        </div>
        <button
          onClick={onGoToOpportunities}
          className="text-sm inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold transition"
          data-testid="jump-to-opportunities"
        >
          See matching funding <ArrowRight size={14} />
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="text-sm inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition"
          data-testid="upload-newer-report"
        >
          {uploading ? <><Loader2 size={14} className="animate-spin" /> Reading…</> : <><Upload size={14} /> Upload newer report</>}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>

      {/* Score delta highlight (shown separately from view for emphasis) */}
      {deltas && deltas.length > 0 && (
        <div className="glass rounded-2xl p-5" data-testid="score-progress-card">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={16} className="text-emerald-400" />
            <h3 className="font-display font-bold text-lg text-white">Progress since last report</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {deltas.map((d, i) => {
              const up = d.delta > 0;
              const flat = d.delta === 0;
              const tone = flat ? "border-slate-500/30 bg-slate-500/10 text-slate-300" : up ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-red-500/40 bg-red-500/10 text-red-300";
              return (
                <div key={i} className={`rounded-xl border p-3 min-w-[140px] ${tone}`} data-testid={`delta-${d.bureau}`}>
                  <p className="text-[10px] uppercase tracking-widest font-bold opacity-70">{d.bureau}</p>
                  <p className="text-xl font-black leading-none mt-0.5">{d.from} → {d.to}</p>
                  <p className="text-sm font-bold mt-1">{d.delta > 0 ? `+${d.delta} pts` : d.delta < 0 ? `${d.delta} pts` : "no change"}</p>
                </div>
              );
            })}
          </div>
          {previous?.created_at && <p className="text-[11px] text-slate-500 mt-3">Compared with report from {fmtDate(previous.created_at)}</p>}
        </div>
      )}

      <CreditReportView report={report} theme="dark" />
    </div>
  );
}

function BenefitCard({ icon: Icon, title, body }) {
  return (
    <div className="glass rounded-2xl p-5">
      <div className="w-10 h-10 rounded-xl bg-white/10 text-amber-300 flex items-center justify-center mb-3"><Icon size={18} /></div>
      <h4 className="font-display font-bold text-white text-base">{title}</h4>
      <p className="text-sm text-slate-400 mt-1">{body}</p>
    </div>
  );
}
